export enum BookingMode {
  RT = 'RT',
  FF = 'FF',
  OWRT = 'OWRT',
  OWFF = 'OWFF',
  CRT = 'CRT',
}

export enum VehicleType {
  CAR = 'CAR',
  BIKE = 'BIKE',
  KICKSCOOTER = 'KICKSCOOTER',
  SCOOTER = 'SCOOTER',
  VAN = 'VAN',
  PLATFORM = 'PLATFORM'
}

export interface QuickRegistrationRequest {
  client_type_id: string
  phone: string
  name: string
  surname: string
  email: string
  birthdate: string
  gender: string
  accepted_tocs_id: string[]
}

export interface LicenseType {
  attachment_views: string[]
  description: string
  has_emission: boolean
  has_expiry: boolean
  has_issuer: boolean
  has_country: boolean
  has_number: boolean
  id: string
  name: string
}

export interface Client {
  id: string
  status: 'ACTIVE' | 'ON HOLD' | 'SUSPENDED' | 'CANCELED'
  name: string
  surname: string
  email: string
  logo_url: null
  phone: string
  mobile: string
  organization_name: string
  address: Address
  billing_address: Address
  fiscal_code: string
  vat_number: string
  client_type: ClientType
  main_driver: Driver
  drivers: Driver[]
  extra: Extra
}

export interface ClientType {
  id: string
  code: string
  type: string
}

export interface Driver {
  id: string
  status: 'ON HOLD' | 'PENDING REVIEW' | 'APPROVED' | 'REFUSED' | 'ACTIVE' | 'SUSPENDED'
  profile_picture: string
  username: string
  title: string
  name: string
  surname: string
  birthdate: string | Date
  gender: string
  email: string
  phone: string
  mobile: string
  address: Address
  main_driver: boolean
  birthplace: string | null
}

export class Address {
  [key: string]: string

  country: string = ''
  region: string = ''
  city: string = ''
  postal_code: string = ''
  street: string = ''
  street_number: string = ''
}

export interface LoginRequest {
  username: string | null
  password: string | null
}

export class LoginResponse {
  public token!: string
  public driver!: Driver
  public pin!: null | string
}

export interface SearchRequest {
  start: number | null
  end: number | null
  booking_mode: string | null
  latitude: number | null
  longitude: number | null
  vehicle_type_id: string | null
  vehicle_category_id: string | null
}

export interface ResetPasswordRequest {
  identification: string | null
}

export interface VehicleSlot {
  id: string
  name: string
  lot: Lot
  reservation_type: BookingMode
  distance: number | null
  position: Position
  vehicle: Vehicle
  base_rate: Rate
}

export interface Plan {
  name?: string
  id: string
}

export interface Lot {
  id: string
  name: string
  description?: string | null
  picture: string
  position: Position
}

export interface Position {
  latitude: number
  longitude: number
}

export interface Pos {
  lat: number
  lng: number
}

export class Vehicle {
  id = ''
  plate = ''
  model = ''
  brand = ''
  brand_logo = ''
  door = ''
  color = ''
  fuel_type: 'PETROL' | 'DIESEL' | 'HYBRID' | 'GAS' | 'ELECTRIC' | 'HYDROGEN' | 'NONE' | 'OTHER' = 'NONE'
  fuel_level: number = 0
  category = new class implements VehicleCategory {
    damage_picture = ''
    description = ''
    id = ''
    name = ''
    picture = ''
    type = '' as VehicleType
  }
  picture = ''
  notes: any[] = []
  attributes: Attribute[] = []
  instructions: instruction[] = []
}

export interface instruction {
  id: string
  steps: instructionStep[]
  title: string
  subtitle: string
}

export interface instructionStep {
  type: string
  value: string
  href?: string
}

export interface VehicleCategory {
  damage_picture: string
  id: string
  name: string
  description: string
  type: VehicleType
  picture: string
}

export interface BookRequest {
  start: number | null
  end: number | null
  plate: string | null
  memo: string | null
}

export interface BookTimeSelectRequest {
  start: number | null
  end: number | null
}

export interface OpenVehicleRequest {
  reservationNumber: number
  pin: string | null
}

export enum ReservationStatus {
  PLANNED = 'PLANNED',
  SENT = 'SENT',
  CONFIRMED = 'CONFIRMED',
  ERROR = 'ERROR',
  CANCELLED = 'CANCELLED',
  STARTED = 'STARTED',
  COMPLETED = 'COMPLETED',
  CHARGED = 'CHARGED',
  SUSPENDED = 'SUSPENDED',
}

export interface ReservationResponse {
  id: string | null
  number: number
  type: string | null
  start: string | null
  end: string | null
  status: ReservationStatus | null
  driver: Driver
  vehicle_slot: VehicleSlot
  trips: Trip[] | null
  cost: number | null
  memo: string | null
  distance: number | null
  created: string | null
  updated: string | null
  start_timestamp: number | null
  end_timestamp: number | null
  created_timestamp: number | null
  updated_timestamp: number | null
  current_vehicle_position: Position | undefined
  extra: { [k: string]: any }
  flags: ReservationFlag[]
  reportings: ReportResponse[]
}

export interface Trip extends Position {
  distance: number
  end: string
  end_timestamp: number
  fuel_level: number
  id: string
  speed: number
  start: string
  start_timestamp: number

}

export interface ReservationFlag {
  reservation_number: number
  flag: string
  value: string
}

export interface AvailabilityRequest {
  start: number
  end: number
  vehicle_id: string
}

export interface AvailabilityResponse {
  start: string
  end: string
  available: boolean
  start_timestamp: number
  end_timestamp: number
  selected: boolean
}

export interface EstimateRequest {
  distance: number,
  start: number,
  end: number,
  plate: string
}

export interface EstimateRequestAll {
  distance: number,
  start: number,
  end: number,
  booking_mode: string
}

export interface Estimate {
  time_cost: number
  distance_cost: number
}

export interface EstimateCategories {
  rate: VehicleCategory
  costs: Estimate
}

// Wallet

export interface Wallet {
  id: string
  balance: number
  name: string
  default: boolean
  payment_method: PaymentMethod
  drivers: Driver[]
  records: {
    [month: string]: {
      [t: string]: WalletRecord[],
    },
  }

  [key: string]: any
}

export interface AvailablePaymentMethod {
  provider: string
  method: string
  notes: string
  logo: string
}

export interface PaymentMethod {
  id: string
  name: string
}

export interface ChangePaymentMethod {
  'payment_method_id': string,
  'wallet_id': string,
}

export interface DeletePaymentMethod {
  'payment_method_id': string,
}

export interface Card {
  name: string
  number: number
  cvv: number
  expiration_date: string
}

// Invoice

export interface Invoice {
  id: string
  number: number
  status: 'ON HOLD' | 'SENT' | 'PAID' | 'CANCELED' | 'OVERDUE' | 'AWAITING PAYMENT' | 'NO PAYMENT METHOD' | 'HOLDOVER PAYMENT',
  payed_on: string | null
  emission_date: string
  due_date: string
  tax: number
  subtotal: number
  total: number
  url: string
  type: 'PROFORMA' | 'INVOICE' | 'CREDIT NOTE',
  billing_info: BillingInfo
}

export interface Deposit {
  id: string
  status: string
  amount: number
  reason: string
  walletable_type: null
  walletable_id: null
  created_at: Date
}

export interface Credit {
  status: string // TODO: expand into the actual values
  name: string
  amount: number
}

export interface BillingInfo {
  client_id: number
  organization_name: string
  vat_number: string
  fiscal_code: string
  country: string
  region: string
  city: string
  postal_code: string
  street: string
}

export interface InvoicePay {
  wallet_id: string
  payment_method_id: string
}

// Subscriptions

export interface Subscription {
  id: string
  subscribed_drivers: number
  auto_renew: boolean
  plan: SubscriptionPlan
  drivers: Driver[]
  toc_id: string
  start: string
  end: string
  next_due_date: string
  start_timestamp: number
  end_timestamp: number
  next_due_date_timestamp: number
}

export interface SubscriptionPlan {
  id: string
  name: string
  title: string
  featured: boolean
  description: string
  cost: number
  status: string
  booking_mode: string
  billing_cycle: number
  max_drivers: number
  duration: number
  valid_from: string
  valid_to: string
  toc_id: string
  rates: Rate[]
  valid_from_timestamp: number
  valid_to_timestamp: number
  renewable?: boolean
}

export interface Rate {
  id: string
  name: string
  plan: Plan
  vehicle_category: VehicleCategory
  time_rate: TimeRate
  distance_rate: DistanceRate
  booking_mode: string
  disabled: number
  is_base_rate: number
  valid_from: null
  valid_to: null
  time_overrides: TimeOverride[]
  distance_overrides: DistanceOverride[]
  bulk_overrides: BulkOverride[]

  [key: string]: any
}

export interface BulkOverride {
  id: string
  name: string
  amount: number
  mon: number
  tue: number
  wed: number
  thu: number
  fri: number
  sat: number
  sun: number
  threshold: number
}

export interface DistanceOverride {
  id: string
  name: string
  description: string
  min_distance: number
  disabled: number
  rate: DistanceRate
}

export interface TimeOverride {
  id: string
  name: string
  description: string
  sun: boolean
  mon: boolean
  tue: boolean
  wed: boolean
  thu: boolean
  fri: boolean
  sat: boolean
  disabled: number
  min_duration: null
  rate: TimeRate
  time_start: string
  time_end: string
}

export interface TimeRate {
  id: string
  name: string
  description: null
  time_cost: number
  driving_time_cost: number
  time_cost_cycle: number
}

export interface DistanceRate {
  id: string
  name: string
  distance_cost: number
  distance_cost_cycle: number
}

// Reports

export interface Report {
  id: string
  type: Type
  status: string
  vehicle: Vehicle
  location: string
  description: string
  damage_position_raw: DamagePositionRaw[] | null
  position: Position
  attachments: any[]
  created: string
  updated: string
  created_timestamp: number
  updated_timestamp: number
}

export interface DamagePositionRaw {
  containerWidth: number
  containerHeight: number
  left: number
  top: number
}

export interface Type {
  id: string
  name: string
  description: string
  position_required: boolean
  attachment_required: boolean
}

export interface LoginByPhoneCodeRequest {
  mobile_number: string | null
}

export interface LoginByPhoneRequest {
  mobile_number: string | null
  token: number | string | null
}

export interface Highlight {
  title: string | null
  content: string | null
  subcontent: string | null
}

export enum FuelTypes {
  PETROL = 'PETROL',
  DIESEL = 'DIESEL',
  HYBRID = 'HYBRID',
  GAS = 'GAS',
  ELECTRIC = 'ELECTRIC',
  HYDROGEN = 'HYDROGEN',
  NONE = 'NONE',
  OTHER = 'OTHER',
}

export interface ModifyRequest {
  name: string
  surname: string
  phone: string
  email: string
  mobile: string
  fiscal_code: string
  vat_number: string
  organization_name: string
  address: Address
  billing_address: Address
  extra: Extra | any
}

export interface Extra {
  actions: ExtraAction[]

  [key: string]: any
}

export enum ExtraAction {
  extend = 'extend',
  edit = 'edit',
  refuel = 'refuel',
  report_unavailable_parking = 'report_unavailable_parking'
}

export interface WalletRecord {
  id: string
  amount: number
  tax: number
  total: number
  pending: boolean
  type: string
  invoice_after: null | string
  reason: string[]
}

export interface DriverInvite {
  name: string
  surname: string
  email: string
}

export interface DriverEditRequest {
  title: string
  name: string
  surname: string
  birthdate: string | Date
  gender: string
  email: string
  phone: string
  mobile: string
  address: Address
}

export enum DocumentState {
  UNVERIFIED = 'UNVERIFIED',
  REFUSED = 'REFUSED',
  EXPIRED = 'EXPIRED',
  VALID = 'VALID',
}

export interface Document {
  id: string
  status: DocumentState
  expiry: string
  issuer: Issuer
  driver: Driver
  type: DocumentType
  attachments: Attachment[]
}

export interface Attachment {
  token: string
  mime: string
  url: string
}

export interface Issuer {
  by: string
  date: string
  country: string
}

export interface DocumentType {
  id: string
  name: string
  description: string
}

export interface TrackRequest extends Position {
  reservation_number: number | null
  accuracy: number
}

export interface TerminatePosition extends Position {
  timestamp: number
  accuracy?: number
  altitude?: number
}

export interface TerminateRequest {
  reservation_number: number
  position?: TerminatePosition
  acceptUndesiderable?: boolean
  forceForbidden?: boolean
  user_declaration?: { [id: string]: boolean }[]
}

export interface KeepRequest {
  reservation_number: number
  amount: string
}

export interface RatesForCategoryRequest {
  planId: string,
  categoryId: string
}

export interface ReportRequest {
  vehicle_id: string,
  reservation_id: string | null,
  reporting_type_id: string | null,
  location: string | null,
  position: Position | null,
  description: string,
  attachments: string[],
  damage_position_raw: DamagePosition[],
}

export interface ReportResponse {
  id: string
  type: Type
  status: string
  vehicle: Vehicle
  location: string
  description: string
  damage_position_raw: DamagePosition[]
  position: Position
  attachments: any[]
  created: string
  updated: string
  created_timestamp: number
  updated_timestamp: number
}

export interface DamagePosition {
  containerWidth: number,
  containerHeight: number,
  left: number,
  top: number,
}

export interface DamagePoints {
  title: string
  left: number,
  top: number,
}

export interface ReportType {
  id: string | null,
  name: string | null,
  description: string | null,
  position_required: boolean | null,
  attachment_required: boolean | null,
}

export interface PaginatedRequest {
  per_page: number
  page_number: number
}

export interface CheckInOut {
  checkInDate: string
  checkOutDate: string
  checkInTime: string
  checkOutTime: string
}

export interface ReservationPaginatedResponse {
  current_page: number
  current_page_count?: number
  items: ReservationResponse[]
  items_count?: number
  page_count?: number
  per_page_count?: number
}

export interface ReservationEditRequest {
  reservation_number: number,
  start: number,
  end: number,
}

export interface ReservationExtendRequest {
  reservation_number: number,
  end: number,
}

export interface BookingMesh {
  booking_mode: string
  vehicle_type: string
  vehicle_type_id: string
}

export interface UserMesh extends BookingMesh {
  available: boolean
}

export interface AppConfig {
  parking_report_id: string | null
  default_language: string | null
  default_latitude: number
  default_longitude: number
  default_plan: string | null
  damage_report_id: string | null
  support_center_url: string
  android_store_url: string
  ios_store_url: string
  currency: string
  sms_login_enabled: boolean | undefined
  round_price_values: boolean
  default_country_code: string
  extra_client_fields: ExtraClientField[]
  fields_configuration: { [key: string]: any }
  prepaid_wallet_enabled?: string | boolean
  time_slot_dimension: number
  web_app_url: string
  mandatory_profile_picture?: boolean

  [key: string]: any
}

export interface ExtraClientField {
  default: string | null
  validation: string
  value: string
}

export interface ProfileImageRequest {
  attachment_token: string
}

export interface AccountStatus {
  driver: boolean,
  client: boolean,
  payment_method: boolean,
  document: boolean,
  signed_tocs: boolean
  subscription: boolean
  driver_status?: 'missing' | 'pending_validation' | 'valid'
  client_status?: 'missing' | 'pending_validation' | 'valid'
  document_status?: 'missing' | 'pending_validation' | 'valid'

  [key: string]: boolean | string | undefined
}

export interface Timestamps {
  client: string,
  client_timestamp: number,
  driver: string,
  driver_timestamp: number,
  drivers: string,
  drivers_timestamp: number,
  documents: string,
  documents_timestamp: number,
  invoice: string,
  invoice_timestamp: number,
  payment_methods: string,
  payment_methods_timestamp: number,
  plans: string,
  plans_timestamp: number,
  reservation: string,
  reservation_timestamp: number,
  subscription: string,
  subscription_timestamp: number,
  wallet_records: string,
  wallet_records_timestamp: number,
  reports: string,
  reports_timestamp: number,

  [key: string]: string | number
}

export interface Autorenew {
  subscription_id: string,
  status?: boolean
}

// ToCs

export interface TocResponse {
  type: TocType
  tocs: TocElement[]
}

export interface TocElement {
  status: 'SIGNED' | 'MISSING' | 'EXPIRED' | 'UPCOMING'
  signed_on: string | null
  document_date: string | null
  document_version: string | null
  toc: TosResponse
}

export interface TosResponse {
  id: string
  type: TocType
  name: null | string
  content: string
  link: null | string
  optional: boolean
  acceptance_statement: null | string
  required_from: null | string
  disabled: boolean
  version: string
  attachment: null | string
}

export interface TocType {
  id: string
  name: string
  group: string
}

export interface TocSignResponse {
  messages: string[]
  result: string
  result_code: string
}

export interface ZoneResponse {
  allowed: Zone
  undesiderable: Zone
  forbidden: Zone
}

export interface Zone {
  id: string
  name: string
  poly: Pos[][]
}

export interface Poi {
  id: string
  name: string
  display_text: string
  icon: string
  visibility: string
  data: PoiData[]
}

export interface PoiData extends Position {
  description: string
  counter: number
}

export interface ParkingLot {
  description: string | null
  id: string
  name: string
  picture: string
  position: Pos
  parking_slots_availability: {
    total: number,
    available: number,
    available_vehicles: number
  }
}

export interface FlowInputValidation {
  id?: string

  [key: string]: any
}

export interface FlowInputStep {
  id: string
  title?: string
  output?: string
  description?: string
  image?: string
  hook?: string
  fields?: FlowInputField[]
  output_format?: { [k: string]: any }
  context?: string
  disable_back?: boolean
  disable_forward?: boolean
  skip?: boolean
}

export interface FlowInputField {
  id: string
  name: string
  description?: string
  type:
    'name'
    | 'surname'
    | 'date'
    | 'email'
    | 'phone'
    | 'string'
    | 'tocs'
    | 'toc'
    | 'phoneVerification'
    | 'summary'
    | 'select'
    | 'image'
    | 'selfie'
    | 'address'
    | 'lang'
    | 'hidden'
    | 'summaryButton'
    | 'document'
    | 'country'
    | 'login'
    | 'attachment'
    | 'paymentMethod'
    | 'qrCode'
    | 'radio_buttons'
  validation?: string | FlowInputValidation
  options?: string[] | Array<{ [key: string]: string }>
  value?: any
}

export class RegistrationImage implements Attachment {
  public url: string = ''
  public mime: string = ''
  public token: string = ''
  public width: number = 512
  public height: number = 512
}

export interface RegistrationRequest {
  endpoint: string,
  payload: any
}

export interface Rating {
  mandatory: boolean
  reporting_type_id: string | null
  survey: string
  rating_threshold: number
}

export interface RatingRequest {
  reservation: number
  ratings: { [key: string]: number }
}

export interface CouponResponse {
  amount: number
  id: string
  reason: string
  tax: number
}

// profile v3

export interface Profile {
  driver: Driver
  data: ProfileData
}

export interface ProfileData {
  wallet?: ProfileItem
  subscriptions?: ProfileItem
  drivers?: ProfileItem
  payment_methods?: ProfileItem
  documents?: ProfileItem
  reportings?: ProfileItem
  tocs?: ProfileItem

  [key: string]: ProfileItem | undefined
}

export interface ProfileItem {
  status: 'FAIL' | 'OK' | 'WARN'
  note: null | string
  flow: null | string
  meta: ProfileItemMeta | WalletMeta
}

export interface ProfileItemMeta {
  count?: null | string | number
  format?: string
  icon?: string

  [key: string]: any
}

export interface WalletMeta extends ProfileItemMeta {
  balance_uninvoiced_records: number
  balance_uninvoiced_deposits: number
  balance_unpaid_invoices: number
  balance_promo_credits: number
  balance_paid_credits: number
}

export interface Attribute {
  id: string
  name: string
  slug: string
  description: string
  recommended: number
  value: string
  type: string
}

export interface Theme {
  options?: Options
  primary: string
  secondary: string
  accent: string
  error: string
  info: string
  success: string
  warning: string

  buttonDefaultColor: string
  buttonDefaultColorOverride?: string

  topBarTop?: string
  topBarBottom?: string
  topBarContrastOverride?: 'dark' | 'light' | string

  filtersTopBarTop?: string
  filtersTopBarBottom?: string
  filtersTopBarContrastOverride?: 'dark' | 'light' | string

  filtersTop?: string
  filtersBottom?: string
  filtersContrastOverride?: 'dark' | 'light' | string

  profileTop: string
  profileBottom: string
  profileContrastOverride?: 'dark' | 'light' | string

  walletTop: string
  walletBottom: string
  walletContrastOverride?: 'dark' | 'light' | string

  topUpTop: string
  topUpBottom: string
  topUpContrastOverride?: 'dark' | 'light' | string

  couponTop: string
  couponBottom: string
  couponContrastOverride?: 'dark' | 'light' | string

  paymentMethodTop: string
  paymentMethodBottom: string
  paymentMethodContrastOverride?: 'dark' | 'light' | string

  driversTop: string
  driversBottom: string
  driversContrastOverride?: 'dark' | 'light' | string

  subscriptionsTop: string
  subscriptionsBottom: string
  subscriptionsContrastOverride?: 'dark' | 'light' | string

  supportTop: string
  supportBottom: string
  supportContrastOverride?: 'dark' | 'light' | string

  planTop: string
  planBottom: string
  planContrastOverride?: 'dark' | 'light' | string

  planDefaultTop?: string
  planDefaultBottom?: string
  planDefaultContrastOverride?: 'dark' | 'light' | string

  subscriptionTop: string
  subscriptionBottom: string
  subscriptionContrastOverride?: 'dark' | 'light' | string

  documentTop: string
  documentBottom: string
  documentContrastOverride?: 'dark' | 'light' | string

  tocTop: string
  tocBottom: string
  tocContrastOverride?: 'dark' | 'light' | string

  overviewWalletTop: string
  overviewWalletBottom: string
  overviewWalletContrastOverride?: 'dark' | 'light' | string

  overviewPrepaidTop: string
  overviewPrepaidBottom: string
  overviewPrepaidContrastOverride?: 'dark' | 'light' | string

  invoiceTop: string
  invoiceBottom: string
  invoiceContrastOverride?: 'dark' | 'light' | string

  RTBIKE: string
  FFBIKE: string
  OWFFBIKE: string
  OWRTBIKE: string
  FFCAR: string
  RTCAR: string
  CRTCAR: string
  OWFFCAR: string
  OWRTCAR: string
  RTSCOOTER: string
  FFSCOOTER: string
  RTKICKSCOOTER: string
  FFKICKSCOOTER: string
  PARKING: string
  RTVAN: string
  RTPLATFORM: string

  missionTop: string
  missionBottom: string
  missionContrastOverride?: 'dark' | 'light' | string
}

export interface Options {
  customProperties: boolean
}

export interface rangeListItem {
  available: boolean,
  selected: boolean
  timestamp: number
}

export type RangeTuple = [number | null, number | null]

export enum HomeModeEnum {
  'map' = 'map',
  'mission' = 'mission'
}

export interface CheckupResponse {
  [action: string]: Survey[]
}

export interface Survey {
  survey: string
  type: string // 'report', 'boolean', 'rating'
  mandatory: boolean
  needs_report: string | null | boolean
  text: string
  text_yes: string | null
  text_no: string | null

  _has_rating: boolean | undefined
  _has_report: boolean | undefined
}

export interface CheckItem {
  id: string
  title: string
  description?: string
  icon?: string
}

export interface CheckpointRequest {
  device: Device
  useragent: string
  appversion: string
  release: string
}

export enum CheckpointStatus {
  'ok' = 'ok',
  'needs-update' = 'needs-update',
  'suggest-update' = 'suggest-update'
}

export interface CheckPointResponse {
  'status': CheckpointStatus
  'update_text'?: string
  'current_level'?: number
  'current_release'?: string
  'android_url'?: string
  'ios_url'?: string
  'browser_url'?: string
}

export interface PositionData {
  latitude: number
  longitude: number
  accuracy: number
  timestamp: string
}

export interface OfflineHookData {
  timestamp: string
  event: string
  fuel_level?: number | undefined
  odometer?: number | undefined
  door_status?: string | undefined
  engine_status?: string | undefined
  position?: PositionData | undefined
}

export interface ReverseGeocoderResponse extends Position {
  result: string
}

export interface OfflineHookData {
  timestamp: string
  event: string
  fuel_level?: number
  odometer?: number
  door_status?: string
  engine_status?: string
  position?: PositionData
}

export interface PositionData {
  latitude: number
  longitude: number
  accuracy: number
  timestamp: string
}

export interface InterventionRequest {
  action: string
  assigned_parking_lot: string
  address: string | null
  distance: number
  plate: string
  fuel_level: string
  reason: string
  score: number
}

export interface InterventionParkingLot {
  distance: number
  parking_lot: {
    id: string
    name: string
    picture: string
    description: string | null
    position: Position
  };
}
